// clip.ts — ingresso del motore delle clip col marchio (SPECIFICA-CLIP.md §3). L'app importa solo da qui.
//
// Come funziona:
// 1. durante il round il registratore disegna le due sorgenti (sopra/sotto, 9:16) su una tela piccola (3/4 della
//    clip) e ne salva fotogrammi JPEG nell'ArchivioFotogrammi (memoria limitata, vedi archivio.ts); l'audio dei due
//    va in un ArchivioAudio;
// 2. a fine round `montaggio()` (funzione pura) decide i tratti e le velocità;
// 3. `creaClip` ridisegna ogni fotogramma della clip a piena risoluzione con le sovrimpressioni e lo codifica:
//    WebCodecs + mp4-muxer (MP4), oppure MediaRecorder in tempo reale come ripiego.
// Il modulo non chiede permessi, non condivide e non salva: restituisce un Blob. Non ferma mai le tracce dell'app.
import { ArchivioAudio, ArchivioFotogrammi, indicePrima, type Fotogramma } from "./archivio.ts";
import { montaAudio } from "./audio-montaggio.ts";
import { avviaCatturaAudio, type CatturaAudio } from "./cattura-audio.ts";
import { caricaMuxer, codificaRegistratore, codificaWebCodecs, scegliAudio, scegliVideo, tipoRegistratore, type Disegnatore } from "./codifica.ts";
import { caricaSimbolo, disegnaSovrimpressioni, telaQr, type Grafica } from "./disegno.ts";
import { comeErroreClip, ErroreClip } from "./errori.ts";
import { montaggio, puntoSorgente } from "./montaggio.ts";
import type { ClipPronta, EventoClip, OpzioniClip, RegistratoreClip, SorgentiClip } from "./tipi.ts";

export type { ColoriClip, OpzioniClip, SorgentiClip, EventoClip, ClipPronta, RegistratoreClip } from "./tipi.ts";
export { ErroreClip, type CodiceErroreClip } from "./errori.ts";

/** Parametri interni (non fanno parte dell'interfaccia). */
const SCALA_CATTURA = 0.75; // la tela di cattura è 3/4 della clip (540×960 per 720×1280)
const QUALITA_JPEG = 0.8; // v040: 0.72 → 0.8 (video più nitido; con il worker non pesa sul filo principale)
const MASSIMO_IN_CODIFICA = 2; // fotogrammi JPEG in compressione nello stesso momento (oltre si salta)
const MASSIMO_IN_CODIFICA_WORKER = 4; // v040: con il worker la compressione non pesa sul filo principale
const FILE_WORKER_JPEG = "/clip-jpeg.js";
const BYTE_MASSIMI = 60 * 1024 * 1024; // tetto dell'archivio dei fotogrammi
const MASSIMO_RADI = 300;
const PASSO_RADO_MS = 250;
const MARGINE_ANELLO_S = 4; // secondi in più nell'anello denso oltre normali + rallentatore
const DOPO_RISATA_MS = 2500; // dopo la risata si registra ancora un poco (finestra del rallentatore), poi basta
const FREQUENZA_AUDIO = 48_000;

const pari = (n: number) => Math.max(2, Math.round(n / 2) * 2);

/** Il telefono può fare clip? E in che formato? */
export function supportoClip(): { possibile: boolean; tipo: string | null; motivo?: string } {
  if (typeof document === "undefined" || typeof HTMLCanvasElement === "undefined")
    return { possibile: false, tipo: null, motivo: "Serve un browser con canvas." };
  if (!("toBlob" in HTMLCanvasElement.prototype)) return { possibile: false, tipo: null, motivo: "Il browser non sa salvare immagini da una tela." };
  if (typeof VideoEncoder !== "undefined" && typeof VideoFrame !== "undefined") return { possibile: true, tipo: "video/mp4" };
  const t = tipoRegistratore();
  if (t) return { possibile: true, tipo: t };
  return { possibile: false, tipo: null, motivo: "Il browser non ha né WebCodecs né la registrazione di una tela (MediaRecorder)." };
}

type Stato = "nuovo" | "registra" | "fermo" | "liberato";

/**
 * v040: compressione JPEG in un worker (OffscreenCanvas.convertToBlob). Prima c'era solo canvas.toBlob sul filo
 * principale: con la videochiamata e l'analisi del volto accese i telefoni lenti facevano pochi fotogrammi al secondo
 * («si vede un fotogramma ogni tanto»). Se il worker non c'è o non funziona si torna a toBlob, da soli.
 */
interface CodificatoreJpeg {
  rotto: boolean;
  codifica(tela: HTMLCanvasElement, qualita: number): Promise<Blob | null>;
  chiudi(): void;
}
function creaCodificatoreJpeg(): CodificatoreJpeg | null {
  if (typeof Worker === "undefined" || typeof OffscreenCanvas === "undefined" || typeof createImageBitmap === "undefined") return null;
  let w: Worker;
  try {
    w = new Worker(FILE_WORKER_JPEG);
  } catch {
    return null;
  }
  let prossimo = 0;
  const attese = new Map<number, (b: Blob | null) => void>();
  const c: CodificatoreJpeg = {
    rotto: false,
    async codifica(tela, qualita) {
      const bitmap = await createImageBitmap(tela);
      return new Promise<Blob | null>((fatto) => {
        const id = ++prossimo;
        attese.set(id, fatto);
        w.postMessage({ id, bitmap, qualita }, [bitmap]);
      });
    },
    chiudi() {
      w.terminate();
      for (const f of attese.values()) f(null);
      attese.clear();
    },
  };
  w.onmessage = (e: MessageEvent<{ id: number; blob: Blob | null }>) => {
    const f = attese.get(e.data.id);
    attese.delete(e.data.id);
    if (!e.data.blob) c.rotto = true;
    f?.(e.data.blob);
  };
  w.onerror = (e) => {
    e.preventDefault?.(); // si torna a toBlob: non è un errore della pagina
    c.rotto = true;
    for (const f of attese.values()) f(null);
    attese.clear();
  };
  return c;
}

export function creaRegistratoreClip(sorgenti: SorgentiClip, opzioni: OpzioniClip, dipendenze?: { adesso?: () => number }): RegistratoreClip {
  const adesso = dipendenze?.adesso ?? (() => performance.now());
  const fpsCattura = Math.min(30, Math.max(1, Math.round(opzioni.fps)));
  const cw = pari(opzioni.larghezza * SCALA_CATTURA);
  const ch = pari(opzioni.altezza * SCALA_CATTURA);
  const vel = Math.min(1, Math.max(0.05, opzioni.rallentatore));
  const secondiAnello = opzioni.secondiPrimaRisata + opzioni.secondiRallentatore * vel + MARGINE_ANELLO_S;
  const archivio = new ArchivioFotogrammi({
    fotogrammiDensi: Math.ceil(secondiAnello * fpsCattura),
    massimoRadi: MASSIMO_RADI,
    passoRadoIniziale: PASSO_RADO_MS,
    contoMassimoMs: 3000,
    byteMassimi: BYTE_MASSIMI,
  });
  let archivioAudio: ArchivioAudio | null = null;
  let cattura: CatturaAudio | null = null;
  let avvioAudio: Promise<void> | null = null;

  let stato: Stato = "nuovo";
  const eventi: EventoClip[] = [];
  let inizio = 0;
  let fineRegistrazione = 0;
  let inPausa = false;
  let smettiDopo = Infinity;
  let inCodifica = 0;
  let ultimaCattura = -Infinity;
  let rafId = 0;
  let timerId: ReturnType<typeof setTimeout> | null = null;
  const inSospeso = new Set<Promise<void>>();

  let contenitore: HTMLDivElement | null = null;
  let videoSopra: HTMLVideoElement | null = null;
  let videoSotto: HTMLVideoElement | null = null;
  // v042: partita a 4 (griglia 2×2)
  const griglia = opzioni.griglia && sorgenti.griglia ? sorgenti.griglia.slice(0, 4) : null;
  const miaCella = griglia ? griglia.indexOf(sorgenti.sotto) : -1;
  let videoCelle: (HTMLVideoElement | null)[] = [];
  let esitoCelle: { riso: number | null; vinto: number | null } | null = null;
  let tela: HTMLCanvasElement | null = null;
  let ctx: CanvasRenderingContext2D | null = null;
  let jpeg: CodificatoreJpeg | null = null;

  const creaVideo = (flusso: MediaStream) => {
    const v = document.createElement("video");
    v.muted = true;
    v.playsInline = true;
    v.autoplay = true;
    v.setAttribute("playsinline", "");
    // Solo le tracce video: l'audio lo prende WebAudio; le tracce restano dell'app.
    v.srcObject = new MediaStream(flusso.getVideoTracks());
    void v.play().catch(() => undefined);
    return v;
  };

  const cattura1 = () => {
    if (!ctx || !tela) return;
    const t = adesso();
    if (t - ultimaCattura < 1000 / fpsCattura - 2) return;
    if (inPausa || t > smettiDopo) return;
    if (inCodifica >= (jpeg && !jpeg.rotto ? MASSIMO_IN_CODIFICA_WORKER : MASSIMO_IN_CODIFICA)) return; // telefono lento: si salta questo fotogramma
    ultimaCattura = t;
    ctx.fillStyle = opzioni.colori.sfondo;
    ctx.fillRect(0, 0, cw, ch);
    if (griglia) {
      const w = cw / 2;
      const h = ch / 2;
      for (let k = 0; k < 4; k++) {
        const x = (k % 2) * w;
        const y = Math.floor(k / 2) * h;
        const v = videoCelle[k];
        if (v && v.readyState >= 2) disegnaVideo(ctx, v, x, y, w, h);
        else cellaVuota(ctx, x, y, w, h);
      }
    } else if (opzioni.solo) {
      if (videoSotto && videoSotto.readyState >= 2) disegnaVideo(ctx, videoSotto, 0, 0, cw, ch);
    } else {
      if (videoSopra && videoSopra.readyState >= 2) disegnaVideo(ctx, videoSopra, 0, 0, cw, ch / 2);
      if (videoSotto && videoSotto.readyState >= 2) disegnaVideo(ctx, videoSotto, 0, ch / 2, cw, ch / 2);
    }
    inCodifica++;
    const salva = (blob: Blob | null) => {
      inCodifica--;
      if (blob && stato !== "liberato") archivio.aggiungi({ t, blob });
    };
    const conToBlob = () =>
      new Promise<void>((fatto) => {
        tela!.toBlob(
          (blob) => {
            salva(blob);
            fatto();
          },
          "image/jpeg",
          QUALITA_JPEG,
        );
      });
    const w = jpeg && !jpeg.rotto ? jpeg : null;
    const p = w
      ? w
          .codifica(tela, QUALITA_JPEG)
          .catch(() => null)
          .then((blob) => {
            if (blob) return salva(blob);
            // il worker non ce la fa (OffscreenCanvas a metà): da qui in poi toBlob
            if (jpeg) jpeg.rotto = true;
            inCodifica--;
          })
      : conToBlob();
    inSospeso.add(p);
    void p.then(() => inSospeso.delete(p));
  };

  const ciclo = () => {
    if (stato !== "registra") return;
    cattura1();
    // requestAnimationFrame quando la pagina è visibile; un timer altrimenti (rAF si ferma in background).
    if (typeof document !== "undefined" && document.visibilityState === "visible" && typeof requestAnimationFrame !== "undefined")
      rafId = requestAnimationFrame(ciclo);
    else timerId = setTimeout(ciclo, 1000 / fpsCattura);
  };

  const smettiCiclo = () => {
    if (rafId && typeof cancelAnimationFrame !== "undefined") cancelAnimationFrame(rafId);
    if (timerId) clearTimeout(timerId);
    rafId = 0;
    timerId = null;
  };

  const togliVideo = () => {
    for (const v of [videoSopra, videoSotto, ...videoCelle]) {
      if (!v) continue;
      v.pause();
      v.srcObject = null; // NON si fermano le tracce: sono dell'app
    }
    contenitore?.remove();
    contenitore = null;
    videoSopra = videoSotto = null;
    videoCelle = [];
  };

  const registratore: RegistratoreClip & { statistiche(): StatisticheClip } = {
    avvia() {
      if (stato !== "nuovo") return;
      stato = "registra";
      inizio = adesso();
      tela = document.createElement("canvas");
      tela.width = cw;
      tela.height = ch;
      ctx = tela.getContext("2d", { alpha: false });
      jpeg = creaCodificatoreJpeg();
      contenitore = document.createElement("div");
      contenitore.setAttribute("aria-hidden", "true");
      contenitore.style.cssText = "position:fixed;left:-9999px;top:0;width:2px;height:2px;overflow:hidden;opacity:0;pointer-events:none";
      // v038: nel video «solo mio» l'altro non c'è proprio (né il video né la voce)
      if (griglia) {
        videoCelle = griglia.map((f) => (f ? creaVideo(f) : null));
        contenitore.append(...videoCelle.filter((v): v is HTMLVideoElement => v !== null));
      } else {
        videoSopra = opzioni.solo || !sorgenti.sopra ? null : creaVideo(sorgenti.sopra);
        videoSotto = creaVideo(sorgenti.sotto);
        contenitore.append(...[videoSopra, videoSotto].filter((v): v is HTMLVideoElement => v !== null));
      }
      document.body.append(contenitore);
      if (opzioni.audio) {
        const conAudio = griglia
          ? griglia.filter((f): f is MediaStream => !!f && f.getAudioTracks().length > 0)
          : opzioni.solo || !sorgenti.sopra
            ? [sorgenti.sotto]
            : [sorgenti.sopra, sorgenti.sotto];
        avvioAudio = avviaCatturaAudio(conAudio.length ? conAudio : [sorgenti.sotto], adesso, (t, dati) => {
          if (!archivioAudio || inPausa || t > smettiDopo || stato !== "registra") return;
          archivioAudio.aggiungi(t, dati);
        }).then((c) => {
          cattura = c;
          if (c) {
            archivioAudio = new ArchivioAudio(c.frequenza, secondiAnello, 3000);
            const via = eventi.find((e) => e.tipo === "via");
            if (via) archivioAudio.segnaVia(via.t);
          }
        });
      }
      ciclo();
    },

    segna(e: EventoClip) {
      if (stato === "liberato") return;
      eventi.push(e);
      if (e.tipo === "via") {
        archivio.segnaVia(e.t);
        archivioAudio?.segnaVia(e.t);
      } else if (e.tipo === "pausa") inPausa = true;
      else if (e.tipo === "ripresa") inPausa = false;
      else if (e.tipo === "fine" && e.celle) esitoCelle = e.celle;
      else if (e.tipo === "risata") {
        inPausa = false;
        archivio.blocca();
        archivioAudio?.blocca();
        smettiDopo = e.t + DOPO_RISATA_MS;
      }
    },

    async ferma() {
      if (stato !== "registra") return;
      stato = "fermo";
      fineRegistrazione = Math.min(adesso(), smettiDopo);
      smettiCiclo();
      await Promise.all([...inSospeso]);
      jpeg?.chiudi();
      jpeg = null;
      if (avvioAudio) await avvioAudio.catch(() => undefined);
      await cattura?.ferma();
      cattura = null;
      togliVideo();
    },

    async creaClip(avanzamento?: (p: number) => void, segnale?: AbortSignal): Promise<ClipPronta> {
      const avanza = avanzamento ?? (() => undefined);
      if (stato === "liberato") throw new ErroreClip("annullata", "Il registratore è già stato liberato.");
      if (stato === "nuovo") throw new ErroreClip("nessuna_risata", "La registrazione non è mai partita: niente clip.");
      if (stato === "registra") await registratore.ferma();
      if (segnale?.aborted) throw new ErroreClip("annullata", "Montaggio annullato.");
      const bitmap = new Map<Fotogramma, ImageBitmap>();
      try {
        const m = montaggio(eventi, opzioni, inizio, fineRegistrazione); // può rifiutare con «nessuna_risata»
        const fotogrammi = archivio.tutti();
        if (!fotogrammi.length) throw new ErroreClip("codifica", "Nessun fotogramma registrato (le sorgenti non davano video?).");
        const fps = Math.min(60, Math.max(1, Math.round(opzioni.fps)));
        const totale = Math.max(1, Math.floor((m.durataMs * fps) / 1000));
        const grafica: Grafica = { simbolo: await caricaSimbolo(opzioni.simbolo), qr: telaQr(`https://${opzioni.link.replace(/^https?:\/\//, "")}`) };

        const telaClip = document.createElement("canvas");
        telaClip.width = pari(opzioni.larghezza);
        telaClip.height = pari(opzioni.altezza);
        const c2 = telaClip.getContext("2d", { alpha: false });
        if (!c2) throw new ErroreClip("non_supportato", "Il browser non dà un contesto 2D.");
        c2.imageSmoothingQuality = "high";

        const prendi = async (f: Fotogramma) => {
          let b = bitmap.get(f);
          if (!b) {
            b = await createImageBitmap(f.blob);
            bitmap.set(f, b);
          }
          return b;
        };
        const tieniSolo = (usati: Set<Fotogramma>) => {
          for (const [f, b] of bitmap) {
            if (!usati.has(f)) {
              b.close();
              bitmap.delete(f);
            }
          }
        };

        const disegnatore: Disegnatore = {
          tela: telaClip,
          totale,
          fps,
          async disegna(i: number) {
            const tClip = (i * 1000) / fps;
            const p = puntoSorgente(m, tClip) ?? puntoSorgente(m, m.durataMs - 1);
            if (!p) return;
            c2.globalAlpha = 1;
            c2.fillStyle = opzioni.colori.sfondo;
            c2.fillRect(0, 0, telaClip.width, telaClip.height);
            if (p.segmento.tipo !== "finale") {
              const k = indicePrima(fotogrammi, p.sorgenteMs);
              const f0 = fotogrammi[k];
              const usati = new Set<Fotogramma>();
              if (f0) {
                usati.add(f0);
                c2.drawImage(await prendi(f0), 0, 0, telaClip.width, telaClip.height);
                // Al rallentatore si mescola col fotogramma successivo: movimento più morbido.
                const f1 = fotogrammi[k + 1];
                if (p.segmento.velocita > 0 && p.segmento.velocita < 1 && f1 && f1.t > f0.t && p.sorgenteMs > f0.t) {
                  const a = Math.min(1, (p.sorgenteMs - f0.t) / (f1.t - f0.t));
                  if (a > 0.02) {
                    usati.add(f1);
                    c2.globalAlpha = a;
                    c2.drawImage(await prendi(f1), 0, 0, telaClip.width, telaClip.height);
                    c2.globalAlpha = 1;
                  }
                }
              }
              tieniSolo(usati);
            }
            disegnaSovrimpressioni(c2, { ...opzioni, larghezza: telaClip.width, altezza: telaClip.height }, grafica, {
              montaggio: m,
              segmento: p.segmento,
              dentroMs: p.dentroMs,
              tClipMs: tClip,
              sorgenteMs: p.sorgenteMs,
              ...(griglia ? { celle: esitoCelle ?? { riso: null, vinto: null } } : {}),
            });
          },
        };

        // Audio montato (se richiesto e registrato).
        const conAudio = opzioni.audio && archivioAudio !== null;
        const campioni = conAudio
          ? montaAudio(m, (da, a) => archivioAudio!.estrai(da, a), FREQUENZA_AUDIO).subarray(0, Math.round((totale / fps) * FREQUENZA_AUDIO))
          : null;

        const sceltaVideo = await scegliVideo(telaClip.width, telaClip.height, fps);
        const sceltaAudio = campioni ? await scegliAudio(FREQUENZA_AUDIO) : null;
        let risultato;
        const conMuxer = sceltaVideo !== null && (await caricaMuxer()) !== null;
        if (sceltaVideo && conMuxer && (!campioni || sceltaAudio)) {
          risultato = await codificaWebCodecs(
            disegnatore,
            sceltaVideo,
            campioni && sceltaAudio ? { scelta: sceltaAudio, campioni, frequenza: FREQUENZA_AUDIO } : null,
            avanza,
            segnale,
          );
        } else if (tipoRegistratore()) {
          risultato = await codificaRegistratore(disegnatore, campioni ? { campioni, frequenza: FREQUENZA_AUDIO } : null, avanza, segnale);
        } else if (sceltaVideo && conMuxer) {
          // WebCodecs c'è ma l'audio no, e manca MediaRecorder: clip muta piuttosto che niente.
          risultato = await codificaWebCodecs(disegnatore, sceltaVideo, null, avanza, segnale);
        } else {
          throw new ErroreClip("non_supportato", "Questo browser non sa codificare video (né WebCodecs né MediaRecorder).");
        }
        return { blob: risultato.blob, tipo: risultato.tipo, durataMs: (totale * 1000) / fps, larghezza: telaClip.width, altezza: telaClip.height };
      } catch (e) {
        throw comeErroreClip(e, "codifica", "La codifica della clip non è riuscita.");
      } finally {
        for (const b of bitmap.values()) b.close();
        bitmap.clear();
      }
    },

    cambiaVideo(quale, flusso) {
      if (griglia) {
        if (quale === "sotto" && miaCella >= 0) registratore.cambiaCella?.(miaCella, flusso);
        return;
      }
      const v = quale === "sopra" ? videoSopra : videoSotto;
      if (!v || stato !== "registra") return;
      const traccia = flusso.getVideoTracks()[0];
      const attuale = (v.srcObject as MediaStream | null)?.getVideoTracks()[0];
      if (!traccia || traccia === attuale) return;
      v.srcObject = new MediaStream([traccia]);
      void v.play().catch(() => undefined);
    },

    cambiaCella(i, flusso) {
      if (!griglia || stato !== "registra" || i < 0 || i > 3) return;
      const v = videoCelle[i];
      if (!flusso) {
        if (v) {
          v.pause();
          v.srcObject = null;
          v.remove();
        }
        videoCelle[i] = null;
        return;
      }
      const traccia = flusso.getVideoTracks()[0];
      if (!traccia) return;
      if (v) {
        if ((v.srcObject as MediaStream | null)?.getVideoTracks()[0] === traccia) return;
        v.srcObject = new MediaStream([traccia]);
        void v.play().catch(() => undefined);
        return;
      }
      const nuovo = creaVideo(flusso);
      videoCelle[i] = nuovo;
      contenitore?.append(nuovo);
    },

    statistiche(): StatisticheClip {
      return {
        byteFotogrammi: archivio.byte(),
        byteAudio: archivioAudio?.byte() ?? 0,
        ...archivio.conteggi(),
        audio: archivioAudio !== null,
        larghezzaCattura: cw,
        altezzaCattura: ch,
        worker: jpeg !== null && !jpeg.rotto,
      };
    },

    libera() {
      if (stato === "liberato") return;
      smettiCiclo();
      jpeg?.chiudi();
      jpeg = null;
      void cattura?.ferma();
      cattura = null;
      togliVideo();
      archivio.svuota();
      archivioAudio?.svuota();
      archivioAudio = null;
      if (tela) {
        tela.width = tela.height = 0;
        tela = null;
      }
      ctx = null;
      stato = "liberato";
    },
  };
  return registratore;
}

/** v042: il riquadro di chi non c'è (o non ha detto sì al video): scuro, con un «?» grande. */
function cellaVuota(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  ctx.save();
  ctx.fillStyle = "#17113a";
  ctx.fillRect(x, y, w, h);
  ctx.globalAlpha = 0.5;
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `900 ${Math.round(w * 0.42)}px system-ui, sans-serif`;
  ctx.fillText("?", x + w / 2, y + h / 2);
  ctx.restore();
}

/** Disegna un <video> coprendo il riquadro (come object-fit: cover). */
function disegnaVideo(ctx: CanvasRenderingContext2D, v: HTMLVideoElement, x: number, y: number, w: number, h: number): void {
  const iw = v.videoWidth;
  const ih = v.videoHeight;
  if (!iw || !ih) return;
  const k = Math.max(w / iw, h / ih);
  const sw = w / k;
  const sh = h / k;
  ctx.drawImage(v, (iw - sw) / 2, (ih - sh) / 2, sw, sh, x, y, w, h);
}

/** Numeri della registrazione, per il banco di prova (non fanno parte dell'interfaccia dell'app). */
export interface StatisticheClip {
  byteFotogrammi: number;
  byteAudio: number;
  conto: number;
  denso: number;
  rado: number;
  passoRado: number;
  audio: boolean;
  larghezzaCattura: number;
  altezzaCattura: number;
  /** v040: i JPEG si fanno nel worker */
  worker?: boolean;
}
